import type { RecurrenceRule } from '../db/models'
import { addDays, dateInMonth, daysBetween, monthsBetween, nthWeekdayInMonth, startOfDay, startOfWeek, weeksBetween } from '../date/calendar'

export type FixedRule = Extract<RecurrenceRule, { type: 'fixed' }>
export type AfterDoneRule = Extract<RecurrenceRule, { type: 'after_done' }>

/**
 * Erste Fälligkeit eines festen Rhythmus am oder nach `from`. `anchor` (das Startdatum der
 * Aufgabe) gibt die Phase: „alle 2 Wochen" heißt in den Wochen des Ankers, nicht in beliebigen.
 * `null` nur bei einer Wochenregel ohne Tage.
 */
export function fixedOccurrenceOnOrAfter(rule: FixedRule, from: Date, anchor: Date): Date | null {
  const start = startOfDay(from)
  const base = startOfDay(anchor)
  const every = Math.max(1, Math.floor(rule.interval))
  switch (rule.unit) {
    case 'day': {
      const gap = daysBetween(base, start)
      if (gap <= 0) return base
      return addDays(base, Math.ceil(gap / every) * every)
    }
    case 'week': {
      const days = [...new Set(rule.weekdays)].sort((a, b) => a - b)
      if (days.length === 0) return null
      const anchorWeek = startOfWeek(base)
      const w0 = Math.max(0, Math.floor(weeksBetween(anchorWeek, startOfWeek(start)) / every) * every)
      for (let w = w0; w <= w0 + every * 2; w += every) {
        const weekStart = addDays(anchorWeek, w * 7)
        for (const wd of days) {
          const candidate = addDays(weekStart, wd)
          if (candidate >= start) return candidate
        }
      }
      return null
    }
    case 'month': {
      const m0 = Math.max(0, Math.floor(monthsBetween(base, start) / every) * every)
      for (let m = m0; m <= m0 + every * 2; m += every) {
        const candidate = rule.nthWeekday
          ? nthWeekdayInMonth(base.getFullYear(), base.getMonth() + m, rule.nthWeekday.nth, rule.nthWeekday.weekday)
          : dateInMonth(base.getFullYear(), base.getMonth() + m, rule.monthDay)
        if (candidate >= start) return candidate
      }
      return null
    }
    case 'year': {
      const y0 = Math.max(0, Math.floor((start.getFullYear() - base.getFullYear()) / every) * every)
      for (let y = y0; y <= y0 + every * 2; y += every) {
        const candidate = dateInMonth(base.getFullYear() + y, rule.yearMonth - 1, rule.yearDay)
        if (candidate >= start) return candidate
      }
      return null
    }
  }
}

/** `completedAt` + n Einheiten, als Kalendertag; Monats-/Jahrestag auf die Monatslänge geklemmt. */
export function afterDoneOccurrence(rule: AfterDoneRule, completedAt: Date): Date {
  const done = startOfDay(completedAt)
  const n = Math.max(1, Math.floor(rule.interval))
  switch (rule.unit) {
    case 'day': return addDays(done, n)
    case 'week': return addDays(done, n * 7)
    case 'month': return dateInMonth(done.getFullYear(), done.getMonth() + n, done.getDate())
    case 'year': return dateInMonth(done.getFullYear() + n, done.getMonth(), done.getDate())
  }
}

/**
 * Der Termin des **Nachfolgers**, nachdem diese Aufgabe abgehakt wurde (ADR 0021).
 *
 * `fixed`: strikt nach dem Späteren von Startdatum und heute — verpasste Termine verfallen,
 * ein Nachfolger kommt nie überfällig zur Welt; zu früh abgehakt springt er hinter den
 * geplanten Tag. `after_done`: vom Erledigen aus. `quota`: morgen, wenn die Aufgabe einen
 * Termin hatte, sonst keiner — Zählen und Vormerken brauchen erst eine Verknüpfung der Nachfolger
 * (docs/concept/wiederkehr-verknuepfung.md; ADR 0021, Nachtrag 2026-09-13).
 */
export function nextOccurrenceAfterCompletion(
  rule: RecurrenceRule,
  ctx: { startDate: Date | null; completedAt: Date },
): Date | null {
  const today = startOfDay(ctx.completedAt)
  switch (rule.type) {
    case 'fixed': {
      const anchor = ctx.startDate ? startOfDay(ctx.startDate) : today
      const from = addDays(anchor > today ? anchor : today, 1)
      return fixedOccurrenceOnOrAfter(rule, from, anchor)
    }
    case 'after_done':
      return afterDoneOccurrence(rule, ctx.completedAt)
    case 'quota':
      return ctx.startDate ? addDays(today, 1) : null
  }
}
